import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const AddEquipmentModal = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    capacity: '',
    unit: 'Liter/Jam',
    age: '',
    condition: 'Baik',
    utilization: '',
    lastMaintenance: '',
    icon: 'Package'
  });

  const [errors, setErrors] = useState({});

  const equipmentIcons = [
    { value: 'Package', label: 'Package' },
    { value: 'Flame', label: 'Flame' },
    { value: 'Snowflake', label: 'Snowflake' },
    { value: 'Droplet', label: 'Droplet' },
    { value: 'Zap', label: 'Zap' },
    { value: 'Wrench', label: 'Wrench' }
  ];

  const conditionOptions = [
    { value: 'Sangat Baik', label: 'Sangat Baik' },
    { value: 'Baik', label: 'Baik' },
    { value: 'Cukup', label: 'Cukup' },
    { value: 'Buruk', label: 'Buruk' },
    { value: 'Kritis', label: 'Kritis' }
  ];

  const unitOptions = [
    { value: 'Liter/Jam', label: 'Liter/Jam' },
    { value: 'Liter/Hari', label: 'Liter/Hari' },
    { value: 'Kg/Jam', label: 'Kg/Jam' },
    { value: 'Unit', label: 'Unit' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.name?.trim()) newErrors.name = 'Nama peralatan wajib diisi';
    if (!formData?.brand?.trim()) newErrors.brand = 'Merek wajib diisi';
    if (!formData?.capacity) newErrors.capacity = 'Kapasitas wajib diisi';
    if (!formData?.age) newErrors.age = 'Usia peralatan wajib diisi';
    if (!formData?.utilization) newErrors.utilization = 'Tingkat utilisasi wajib diisi';
    if (formData?.utilization && (formData?.utilization < 0 || formData?.utilization > 100)) {
      newErrors.utilization = 'Utilisasi harus antara 0-100%';
    }
    if (!formData?.lastMaintenance) newErrors.lastMaintenance = 'Tanggal pemeliharaan terakhir wajib diisi';

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (validateForm()) {
      const newEquipment = {
        id: Date.now(),
        ...formData,
        capacity: parseFloat(formData?.capacity),
        age: parseInt(formData?.age),
        utilization: parseFloat(formData?.utilization)
      };
      
      onSave(newEquipment);
      handleClose();
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      brand: '',
      capacity: '',
      unit: 'Liter/Jam',
      age: '',
      condition: 'Baik',
      utilization: '',
      lastMaintenance: '',
      icon: 'Package'
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-xl shadow-2xl border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="Plus" size={20} color="var(--color-primary)" />
            </div>
            <h2 className="text-xl font-semibold text-foreground">Tambah Peralatan Baru</h2>
          </div>
          <button
            onClick={handleClose}
            className="p-2 rounded-lg hover:bg-muted transition-colors focus-ring"
            aria-label="Close modal"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Nama Peralatan"
              type="text"
              name="name"
              value={formData?.name}
              onChange={handleInputChange}
              error={errors?.name}
              placeholder="Contoh: Mesin Pasteurisasi"
              required
            />

            <Input
              label="Merek/Produsen"
              type="text"
              name="brand"
              value={formData?.brand}
              onChange={handleInputChange}
              error={errors?.brand}
              placeholder="Contoh: Tetra Pak"
              required
            />

            <Input
              label="Kapasitas"
              type="number"
              name="capacity"
              value={formData?.capacity}
              onChange={handleInputChange}
              error={errors?.capacity}
              placeholder="Contoh: 500"
              required
            />

            <Select
              label="Satuan Kapasitas"
              options={unitOptions}
              value={formData?.unit}
              onChange={(value) => handleSelectChange('unit', value)}
            />

            <Input
              label="Usia Peralatan (Tahun)"
              type="number"
              name="age"
              value={formData?.age}
              onChange={handleInputChange}
              error={errors?.age}
              placeholder="Contoh: 5"
              required
            />

            <Select
              label="Kondisi"
              options={conditionOptions}
              value={formData?.condition}
              onChange={(value) => handleSelectChange('condition', value)}
            />

            <Input
              label="Tingkat Utilisasi (%)"
              type="number"
              name="utilization"
              value={formData?.utilization}
              onChange={handleInputChange}
              error={errors?.utilization}
              placeholder="Contoh: 85"
              description="Persentase penggunaan dari kapasitas maksimal"
              required
            />

            <Input
              label="Pemeliharaan Terakhir"
              type="date"
              name="lastMaintenance"
              value={formData?.lastMaintenance}
              onChange={handleInputChange}
              error={errors?.lastMaintenance}
              required
            />

            <Select
              label="Ikon Peralatan"
              options={equipmentIcons}
              value={formData?.icon}
              onChange={(value) => handleSelectChange('icon', value)}
              description="Pilih ikon yang mewakili jenis peralatan"
            />
          </div>

          <div className="flex items-center gap-4 pt-6 border-t border-border">
            <Button
              type="button"
              variant="outline"
              size="lg"
              fullWidth
              onClick={handleClose}
            >
              Batal
            </Button>
            <Button
              type="submit"
              variant="default"
              size="lg"
              fullWidth
              iconName="Save"
              iconPosition="left"
            >
              Simpan Peralatan
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEquipmentModal;